import { useState, useEffect, useRef } from "react";

// ─── COPY DATA ───────────────────────────────────────────────────────────────

const HERO = {
  eyebrow: "Documentation Readiness Platform",
  headline: "Catch documentation gaps before they become denials, rework, and delays.",
  sub: "Caldarium helps provider teams check clinical documentation against payer requirements, surface missing evidence, and prepare stronger prior authorization submissions before they go out. By preventing documentation-related denials, teams reduce rework, accelerate approvals, and protect revenue.",
  benefits: [
    "Flag gaps before submission",
    "Reduce rework and delays",
    "Keep humans in control",
  ],
  cta1: "Book a Demo",
  cta2: "See a Sample Case Review",
};

const DEMO_ITEMS = [
  { label: "Clinical history & onset documented", status: "Supported" },
  { label: "Conservative treatment evidence", status: "Supported" },
  { label: "Functional limitation detail", status: "Partial" },
  { label: "Duration of symptoms", status: "Missing" },
  { label: "Prior imaging results referenced", status: "Missing" },
];

const PROBLEMS = [
  {
    title: "Revenue leakage from denials",
    body: "Documentation-driven denials translate directly into lost revenue. For health systems, denial rates show up in financial filings — and the cost compounds fast on high-margin procedures like transplants and cardiac care.",
  },
  {
    title: "Policy complexity at scale",
    body: "Requirements differ by payer, plan, and procedure. Staff manually interpret shifting criteria without consistent tooling — and CMS 0057 is raising the bar for electronic prior auth, creating urgency to modernize now.",
  },
  {
    title: "Overstretched teams",
    body: "Patient access and authorization staff carry unsustainable caseloads. Cases queue over weekends with no payer-side review. Turnaround windows are tightening to 72 hours urgent and 7 calendar days standard.",
  },
];

const ROI_CARDS = [
  {
    title: "Fewer avoidable denials",
    body: "Reduce documentation-related denials before submission, especially on high-value procedures where missing evidence is costly.",
  },
  {
    title: "Less rework per case",
    body: "Avoid repeated review, resubmission, and appeal cycles that consume staff time and slow throughput.",
  },
  {
    title: "Faster authorization turnaround",
    body: "Submit cleaner cases upfront and reduce delays tied to incomplete or poorly aligned documentation.",
  },
  {
    title: "Recovered revenue",
    body: "Protect revenue that would otherwise be lost to denials, delays, and preventable documentation gaps.",
  },
];

const WORKFLOW_STEPS = [
  {
    num: 1,
    title: "Intake the case",
    body: "Start with an authorization request or order. Caldarium captures the clinical context, procedure details, and payer information needed to begin review.",
  },
  {
    num: 2,
    title: "Surface payer requirements",
    body: "Identify the documentation elements and evidence categories that matter for this specific request type, payer, and plan.",
  },
  {
    num: 3,
    title: "Review available documentation",
    body: "Examine clinical notes, order details, and chart materials — including the unstructured evidence that's hardest to find and assemble.",
  },
  {
    num: 4,
    title: "Map evidence to requirements",
    body: "Show where documentation supports the request and where critical evidence is missing or incomplete.",
  },
  {
    num: 5,
    title: "Flag gaps before submission",
    body: "Surface what's still needed so the request doesn't go out with avoidable weaknesses that lead to denials.",
  },
  {
    num: 6,
    title: "Prepare the submission packet",
    body: "Turn the case into a cleaner, better-organized, more defensible request — ready for payer review.",
  },
];

const FEATURES = [
  {
    title: "Gap Detection",
    body: "Surface missing or incomplete evidence before submission, preventing avoidable denials before the case reaches the payer.",
  },
  {
    title: "Policy-Aligned Review",
    body: "Structure every case review around what the payer actually requires for that procedure and plan.",
  },
  {
    title: "Evidence Mapping",
    body: "Connect clinical documentation to medical necessity requirements — visually and systematically.",
  },
  {
    title: "Submission Readiness",
    body: "Know whether a case is truly ready to submit, or likely to be delayed or denied, before it goes out.",
  },
  {
    title: "Human-in-the-Loop",
    body: "Every output is transparent, reviewable, and designed for human oversight — not black-box automation.",
  },
  {
    title: "Audit-Ready Records",
    body: "Organize documentation so it is easier for payers to approve and harder to deny.",
  },
];

const USE_CASES = [
  {
    tag: "Initial focus",
    title: "Radiology & Imaging Authorization",
    body: "Imaging prior auth — lumbar MRI, advanced imaging, CT — involves predictable documentation standards and frequent evidence gaps. This is where Caldarium starts.",
  },
  {
    tag: "High value",
    title: "High-Margin Procedure Authorization",
    body: "Transplants, cardiac procedures, and other high-value services carry the largest financial impact when documentation gaps lead to denials or delays.",
  },
  {
    tag: "Specialty care",
    title: "Specialty Medication Authorization",
    body: "Specialty drugs and infusion therapies often require extensive clinical justification, step therapy history, and supporting documentation. Caldarium helps teams prepare cleaner submissions before they reach the payer.",
  },
  {
    tag: "Operational priority",
    title: "Surgical & Interventional Authorization",
    body: "Authorizations for surgical and interventional procedures can create significant delay and revenue risk when documentation is incomplete or poorly aligned with payer criteria. Caldarium helps teams catch those gaps before submission.",
  },
];

const SOUND_FAMILIAR = [
  "We submit requests missing key evidence — and don't realize until the denial comes back.",
  "Staff are reworking cases that should have been cleaner upfront.",
  "Payer requirements vary and are almost impossible to interpret consistently across the team.",
  "Critical evidence is buried in the chart but nobody can assemble it fast enough.",
  "We're losing real revenue to documentation denials on procedures we know are medically necessary.",
  "We need a practical AI tool — not a science project — and compliance has to sign off.",
];

const APPROACH = [
  {
    title: "Human-in-the-loop by design",
    body: "The platform supports human review at every step. It does not replace clinical judgment — it makes clinical documentation easier to organize and review.",
  },
  {
    title: "Built for unstructured clinical evidence",
    body: "The most important evidence lives in notes, encounter summaries, and documentation that's difficult to parse manually. Caldarium is built for that reality.",
  },
  {
    title: "Policy-to-evidence alignment",
    body: "Organized around the relationship between what payers require and what actually exists in the chart — bridging the gap that causes denials.",
  },
  {
    title: "Provider-side operations focus",
    body: "Designed for the teams that carry the authorization burden: patient access, revenue cycle, utilization management, and clinical operations.",
  },
  {
    title: "Interoperability-aware",
    body: "Built with FHIR, Da Vinci implementation guides, and modern electronic prior authorization infrastructure in mind.",
  },
  {
    title: "Audit-defensible and explainable",
    body: "Outputs are structured so staff can understand, review, and verify every recommendation before acting on it.",
  },
];

const REG_TAGS = [
  "FHIR-aware design",
  "Da Vinci IG alignment",
  "CMS 0057 readiness",
  "e-Prior auth trajectory",
  "Structured report support",
  "Payer connectivity aware",
];

const PERSONAS = [
  {
    title: "Patient Access & Authorization Teams",
    body: "Managing intake, documentation gathering, and submission across dozens of cases daily.",
  },
  {
    title: "Revenue Cycle Leaders",
    body: "Reducing administrative waste, improving turnaround, and preventing documentation-driven denials that erode margin.",
  },
  {
    title: "Utilization Management",
    body: "Chart review, documentation assessment, and supporting clinical justification for requested services.",
  },
  {
    title: "Clinical Operations",
    body: "Clinicians and support staff who contribute documentation and need confidence it's complete before authorization.",
  },
  {
    title: "CIOs & Digital Health Leaders",
    body: "Evaluating AI tools that can be deployed safely and practically within governance-friendly environments.",
  },
  {
    title: "Compliance & Quality",
    body: "Stakeholders who need confidence that workflow tools meet trust, audit, and oversight standards.",
  },
];

const FAQS = [
  {
    q: "Does Caldarium replace staff or clinicians?",
    a: "No. Caldarium supports staff and clinicians by helping organize and review documentation before submission. Human judgment remains central to the workflow.",
  },
  {
    q: "Is this fully autonomous prior authorization?",
    a: "No. Caldarium focuses on documentation readiness — making sure the submission is supported by the right clinical evidence before it goes out. It does not submit or decide on behalf of your team.",
  },
  {
    q: "How does this relate to CMS 0057 and electronic prior auth?",
    a: "CMS 0057 and Da Vinci implementation guides are moving the industry toward electronic prior authorization workflows. Caldarium helps ensure documentation quality regardless of how requests are transmitted — by fax, portal, or future electronic channels.",
  },
  {
    q: "Who is the product designed for?",
    a: "Caldarium is built for provider-side teams including patient access, authorization teams, revenue cycle, utilization management, and clinical operations leaders.",
  },
  {
    q: "How does Caldarium handle AI and trust?",
    a: "All outputs are transparent and reviewable. Caldarium is designed for human oversight — every recommendation can be examined, verified, and overridden before action is taken. No black-box decisions.",
  },
  {
    q: "Does it integrate with our EHR?",
    a: "Caldarium is being designed with interoperability in mind, including modern healthcare data standards like FHIR. Specific EHR integrations will be developed as the platform matures — we do not overclaim what isn't built yet.",
  },
  {
    q: "Is Caldarium already deployed at hospitals?",
    a: "Caldarium is an early-stage platform being developed with input from healthcare operators and technology leaders. We're working with provider organizations to shape the product around real workflows.",
  },
  {
    q: "What types of denials does Caldarium impact?",
    a: "Caldarium focuses on documentation-related denials — cases where the care may be appropriate but the supporting evidence is incomplete, missing, or misaligned at the time of submission.",
  },
  {
    q: "How can you help reduce our denial rate?",
    a: "By catching documentation gaps before submission, Caldarium helps your team send stronger, more complete authorization requests — reducing the avoidable denials that come from missing or misaligned evidence.",
  },
];

// ─── ICONS ───────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <circle cx="9" cy="9" r="9" fill="#00FF7D" opacity="0.15" />
    <path d="M5.5 9.5L7.5 11.5L12.5 6.5" stroke="#00FF7D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDown = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ transition: "transform 0.25s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StatusDot = ({ status }) => {
  const colors = { Supported: "#00FF7D", Partial: "#FFB800", Missing: "#FF4D4D" };
  return <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: colors[status] || "#888", marginRight: 8, flexShrink: 0 }} />;
};

// ─── COMPONENTS ──────────────────────────────────────────────────────────────

function FadeIn({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: `opacity 0.6s ${delay}s, transform 0.6s ${delay}s` }}>
      {children}
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 0", background: "none", border: "none", color: "#fff", fontSize: 16, fontFamily: "'Nunito Sans', sans-serif", textAlign: "left", cursor: "pointer", gap: 16 }}>
        <span style={{ fontWeight: 600 }}>{q}</span>
        <ChevronDown open={open} />
      </button>
      <div style={{ maxHeight: open ? 300 : 0, overflow: "hidden", transition: "max-height 0.35s ease" }}>
        <p style={{ margin: 0, padding: "0 0 20px", color: "rgba(255,255,255,0.65)", lineHeight: 1.7, fontSize: 15 }}>{a}</p>
      </div>
    </div>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────

export default function CaldHome2() {
  const CTA_LINK = "https://calendly.com"; // Replace with Kevin's Calendly link

  return (
    <div style={{ background: "#111", color: "#fff", fontFamily: "'Nunito Sans', sans-serif", overflowX: "hidden" }}>
      {/* ── Google Fonts ── */}
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=Nunito+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --green: #00FF7D;
          --green2: #6DFFB5;
          --green3: #DBFFEC;
          --charcoal: #414042;
          --gray1: #808184;
          --gray2: #BBBDBF;
          --gray3: #F1F1F2;
          --bg: #0D0F0E;
          --card: rgba(255,255,255,0.04);
          --border: rgba(255,255,255,0.08);
        }
        html { scroll-behavior: smooth; }
        .syne { font-family: 'Syne', sans-serif; }
        .section { max-width: 1120px; margin: 0 auto; padding: 100px 24px; }
        .section-sm { max-width: 860px; margin: 0 auto; padding: 100px 24px; }
        .eyebrow { font-size: 13px; letter-spacing: 2.5px; text-transform: uppercase; color: var(--green); font-weight: 600; margin-bottom: 16px; }
        .h2 { font-family: 'Syne', sans-serif; font-size: clamp(28px,4vw,42px); font-weight: 700; line-height: 1.2; margin-bottom: 20px; }
        .sub { color: rgba(255,255,255,0.6); font-size: 17px; line-height: 1.7; max-width: 640px; }
        .btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 14px 28px; background: var(--green); color: #111; font-weight: 700; font-size: 15px; border: none; border-radius: 6px; cursor: pointer; text-decoration: none; transition: background 0.2s, transform 0.15s; }
        .btn-primary:hover { background: var(--green2); transform: translateY(-1px); }
        .btn-secondary { display: inline-flex; align-items: center; gap: 8px; padding: 14px 28px; background: transparent; color: #fff; font-weight: 600; font-size: 15px; border: 1px solid rgba(255,255,255,0.2); border-radius: 6px; cursor: pointer; text-decoration: none; transition: border-color 0.2s, transform 0.15s; }
        .btn-secondary:hover { border-color: var(--green); color: var(--green); transform: translateY(-1px); }
        .card { background: var(--card); border: 1px solid var(--border); border-radius: 12px; padding: 28px; }
        .grid-2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px,1fr)); gap: 20px; }
        .grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px,1fr)); gap: 20px; }
        @media(max-width:640px) {
          .section, .section-sm { padding: 64px 18px; }
          .grid-2, .grid-3 { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* ═══════════════════ NAV ═══════════════════ */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(13,15,14,0.85)", backdropFilter: "blur(16px)", borderBottom: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "0 24px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
          <span className="syne" style={{ fontSize: 20, fontWeight: 700, color: "#fff", letterSpacing: -0.5 }}>Caldarium</span>
          <div style={{ display: "flex", gap: 28, alignItems: "center", fontSize: 14, fontWeight: 500 }}>
            {["Platform", "Use Cases", "Approach", "About", "FAQ"].map((t) => (
              <a key={t} href={`#${t.toLowerCase().replace(/ /g, "-")}`} style={{ color: "rgba(255,255,255,0.6)", textDecoration: "none", transition: "color 0.2s" }} onMouseEnter={(e) => (e.target.style.color = "#fff")} onMouseLeave={(e) => (e.target.style.color = "rgba(255,255,255,0.6)")}>
                {t}
              </a>
            ))}
            <a href={CTA_LINK} className="btn-primary" style={{ padding: "8px 20px", fontSize: 13 }}>Book a Demo</a>
          </div>
        </div>
      </nav>

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section style={{ position: "relative", overflow: "hidden" }}>
        {/* subtle gradient glow */}
        <div style={{ position: "absolute", top: -200, right: -200, width: 600, height: 600, background: "radial-gradient(circle, rgba(0,255,125,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div className="section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56, alignItems: "center", paddingTop: 80, paddingBottom: 80 }}>
          <FadeIn>
            <p className="eyebrow">{HERO.eyebrow}</p>
            <h1 className="syne" style={{ fontSize: "clamp(32px,4.5vw,50px)", fontWeight: 800, lineHeight: 1.15, marginBottom: 20 }}>{HERO.headline}</h1>
            <p className="sub" style={{ marginBottom: 28 }}>{HERO.sub}</p>
            <div style={{ display: "flex", gap: 24, marginBottom: 32, flexWrap: "wrap" }}>
              {HERO.benefits.map((b) => (
                <span key={b} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "rgba(255,255,255,0.7)" }}>
                  <CheckIcon /> {b}
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <a href={CTA_LINK} className="btn-primary">{HERO.cta1}</a>
              <a href="#platform" className="btn-secondary">{HERO.cta2}</a>
            </div>
            <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 6 }}>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>Built to prevent avoidable, documentation-driven denials before submission.</p>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.6 }}>As prior authorization shifts toward electronic workflows under CMS 0057, documentation quality is becoming the limiting factor.</p>
            </div>
          </FadeIn>
          {/* Demo card */}
          <FadeIn delay={0.15}>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginBottom: 12, fontWeight: 500 }}>Example: what your team sees before submission</p>
            <div className="card" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "var(--green)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Submission Readiness Review</p>
                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }}>Lumbar MRI — Radiology Prior Auth</p>
                </div>
                <span style={{ fontSize: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(255,184,0,0.12)", color: "#FFB800", fontWeight: 600 }}>In Review</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {DEMO_ITEMS.map((d) => (
                  <div key={d.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 8, background: "rgba(255,255,255,0.03)", fontSize: 14 }}>
                    <span style={{ color: "rgba(255,255,255,0.75)" }}>{d.label}</span>
                    <span style={{ display: "flex", alignItems: "center", fontSize: 13, fontWeight: 600, color: d.status === "Supported" ? "#00FF7D" : d.status === "Partial" ? "#FFB800" : "#FF4D4D" }}>
                      <StatusDot status={d.status} /> {d.status}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 8, background: "rgba(255,77,77,0.08)", border: "1px solid rgba(255,77,77,0.15)", fontSize: 13, color: "#FF8A8A" }}>
                ⚠ 3 documentation gaps detected — review before submission
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════ PROBLEM ═══════════════════ */}
      <section style={{ background: "rgba(0,0,0,0.3)" }}>
        <div className="section">
          <FadeIn>
            <p className="eyebrow">The Problem</p>
            <h2 className="h2">Most prior authorization failures are documentation failures — not clinical ones.</h2>
            <p className="sub" style={{ marginBottom: 48 }}>Treatments get delayed or denied not because the care is wrong, but because the supporting documentation is incomplete, fragmented, or misaligned with what the payer requires. These failures are predictable, and preventable.</p>
          </FadeIn>
          <div className="grid-3">
            {PROBLEMS.map((p, i) => (
              <FadeIn key={p.title} delay={i * 0.1}>
                <div className="card" style={{ height: "100%" }}>
                  <h3 className="syne" style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: "#fff" }}>{p.title}</h3>
                  <p style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.7, fontSize: 15 }}>{p.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ ROI ═══════════════════ */}
      <section id="roi">
        <div className="section">
          <FadeIn>
            <p className="eyebrow">What This Means for Your Team</p>
            <h2 className="h2">Documentation gaps create measurable financial and operational drag.</h2>
            <p className="sub" style={{ marginBottom: 48 }}>For provider organizations, documentation-related denials do not just create friction. They create rework, delay care, and put revenue at risk.</p>
          </FadeIn>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px,1fr))", gap: 20, marginBottom: 32 }}>
            {ROI_CARDS.map((c, i) => (
              <FadeIn key={c.title} delay={i * 0.08}>
                <div className="card" style={{ height: "100%", borderTop: "2px solid var(--green)" }}>
                  <h3 className="syne" style={{ fontSize: 17, fontWeight: 700, marginBottom: 10, color: "#fff" }}>{c.title}</h3>
                  <p style={{ color: "rgba(255,255,255,0.6)", lineHeight: 1.7, fontSize: 14 }}>{c.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.35}>
            <div className="card" style={{ background: "rgba(0,255,125,0.04)", borderColor: "rgba(0,255,125,0.15)" }}>
              <h4 className="syne" style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: "var(--green)" }}>Example impact</h4>
              <p style={{ color: "rgba(255,255,255,0.65)", fontSize: 15, lineHeight: 1.7 }}>A provider organization processing high volumes of prior authorizations can lose meaningful revenue and staff time to documentation-related denials and rework. Caldarium is designed to reduce those avoidable losses by helping teams send stronger cases the first time.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════ PLATFORM ═══════════════════ */}
      <section id="platform">
        <div className="section">
          <FadeIn>
            <p className="eyebrow">Platform</p>
            <h2 className="h2">From fragmented chart to submission-ready, approval-ready case.</h2>
            <p className="sub" style={{ marginBottom: 56 }}>Caldarium organizes clinical documentation against payer requirements, helping your team see what is supported, what is missing, and what needs attention before submission. The goal is simple: send stronger cases the first time and avoid preventable denials.</p>
          </FadeIn>

          {/* workflow steps */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px,1fr))", gap: 20, marginBottom: 56 }}>
            {WORKFLOW_STEPS.map((s, i) => (
              <FadeIn key={s.num} delay={i * 0.07}>
                <div className="card" style={{ height: "100%", position: "relative", paddingTop: 36 }}>
                  <span className="syne" style={{ position: "absolute", top: -1, left: -1, background: "var(--green)", color: "#111", fontSize: 12, fontWeight: 800, padding: "5px 12px", borderRadius: "12px 0 8px 0" }}>{s.num}</span>
                  <h4 className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{s.title}</h4>
                  <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.65 }}>{s.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* feature cards */}
          <div className="grid-3">
            {FEATURES.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.06}>
                <div style={{ padding: "20px 0", borderTop: "1px solid var(--border)" }}>
                  <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: "var(--green)" }}>{f.title}</h4>
                  <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.65 }}>{f.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ USE CASES ═══════════════════ */}
      <section id="use-cases" style={{ background: "rgba(0,0,0,0.3)" }}>
        <div className="section">
          <FadeIn>
            <p className="eyebrow">Use Cases</p>
            <h2 className="h2">Starting where documentation quality hits revenue hardest.</h2>
            <p className="sub" style={{ marginBottom: 48 }}>Caldarium starts where documentation quality has the greatest financial and operational impact on approvals, revenue, and patient throughput.</p>
          </FadeIn>
          <div className="grid-2">
            {USE_CASES.map((u, i) => (
              <FadeIn key={u.title} delay={i * 0.08}>
                <div className="card" style={{ height: "100%" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--green)", textTransform: "uppercase", letterSpacing: 2 }}>{u.tag}</span>
                  <h3 className="syne" style={{ fontSize: 18, fontWeight: 700, margin: "10px 0 10px" }}>{u.title}</h3>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 14, lineHeight: 1.7 }}>{u.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.3}>
            <div className="card" style={{ marginTop: 20, background: "rgba(0,255,125,0.04)", borderColor: "rgba(0,255,125,0.12)" }}>
              <p style={{ fontSize: 15, color: "rgba(255,255,255,0.7)", lineHeight: 1.7 }}>
                <strong style={{ color: "#fff" }}>Especially relevant for rural and trauma hospitals.</strong> Organizations with lean teams, high denial rates, and limited margin for administrative rework stand to benefit the most from documentation-readiness tooling.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.35}>
            <div style={{ marginTop: 36 }}>
              <a href={CTA_LINK} className="btn-secondary">Discuss Your Workflow</a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════ SOUND FAMILIAR ═══════════════════ */}
      <section>
        <div className="section-sm">
          <FadeIn>
            <p className="eyebrow">Sound Familiar?</p>
            <h2 className="h2">We built Caldarium for teams dealing with this every day.</h2>
          </FadeIn>
          <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 32 }}>
            {SOUND_FAMILIAR.map((line, i) => (
              <FadeIn key={i} delay={i * 0.06}>
                <div style={{ padding: "18px 0", borderBottom: "1px solid var(--border)", display: "flex", gap: 16, alignItems: "baseline" }}>
                  <span style={{ color: "var(--green)", fontSize: 20, lineHeight: 1, flexShrink: 0 }}>"</span>
                  <p style={{ fontSize: 16, color: "rgba(255,255,255,0.7)", lineHeight: 1.6, fontStyle: "italic" }}>{line}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ APPROACH ═══════════════════ */}
      <section id="approach" style={{ background: "rgba(0,0,0,0.3)" }}>
        <div className="section">
          <FadeIn>
            <p className="eyebrow">Our Approach</p>
            <h2 className="h2">Preventing denials starts with documentation, not automation.</h2>
            <p className="sub" style={{ marginBottom: 48 }}>Caldarium is built around a simple premise: stronger documentation before submission leads to fewer avoidable denials after. Every design decision follows from that.</p>
          </FadeIn>
          <div className="grid-2">
            {APPROACH.map((a, i) => (
              <FadeIn key={a.title} delay={i * 0.07}>
                <div style={{ padding: "20px 0", borderTop: "1px solid var(--border)" }}>
                  <h4 className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{a.title}</h4>
                  <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 15, lineHeight: 1.7 }}>{a.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ REGULATORY ═══════════════════ */}
      <section>
        <div className="section-sm">
          <FadeIn>
            <p className="eyebrow">Regulatory Awareness</p>
            <h2 className="h2">Electronic prior auth is coming. Documentation readiness is how providers get there.</h2>
            <p className="sub" style={{ marginBottom: 20 }}>CMS 0057 and the Da Vinci implementation guides are reshaping prior authorization — moving toward electronic, FHIR-based workflows between payers and providers. The three components of this transition are Coverage Requirements Discovery (CRD), Documentation Templates and Rules (DTR), and Prior Authorization Support (PAS).</p>
            <p className="sub" style={{ marginBottom: 36 }}>Infrastructure alone will not fix prior authorization if documentation quality is still weak. Providers still need higher-quality submissions regardless of how the request is transmitted. Caldarium helps teams prepare evidence that meets the bar today — and as standards evolve.</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {REG_TAGS.map((t) => (
                <span key={t} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid rgba(0,255,125,0.2)", fontSize: 13, color: "var(--green)", fontWeight: 600 }}>{t}</span>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════ WHO IT'S FOR ═══════════════════ */}
      <section style={{ background: "rgba(0,0,0,0.3)" }}>
        <div className="section">
          <FadeIn>
            <p className="eyebrow">Who It's For</p>
            <h2 className="h2">Built for the teams that carry the prior authorization burden.</h2>
            <p className="sub" style={{ marginBottom: 12 }}>Caldarium is designed for the teams responsible for authorization outcomes and revenue integrity.</p>
          </FadeIn>
          <div className="grid-3">
            {PERSONAS.map((p, i) => (
              <FadeIn key={p.title} delay={i * 0.07}>
                <div className="card" style={{ height: "100%" }}>
                  <h4 className="syne" style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{p.title}</h4>
                  <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 14, lineHeight: 1.65 }}>{p.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ ABOUT ═══════════════════ */}
      <section id="about">
        <div className="section-sm">
          <FadeIn>
            <p className="eyebrow">About Caldarium</p>
            <h2 className="h2">Many of healthcare's administrative failures are actually information failures.</h2>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 16, lineHeight: 1.8 }}>
              <p style={{ marginBottom: 16 }}>Prior authorization is one of the clearest examples: critical decisions depend on documentation quality, evidence is scattered across encounters and systems, staff time is consumed by rework, and patients face avoidable delays.</p>
              <p style={{ marginBottom: 16 }}>Caldarium was created around the belief that the right clinical evidence usually exists — it's just too fragmented, too manual, or too difficult to assemble in time.</p>
              <p>This is not about replacing human judgment. It's about giving provider teams better tools for one of the most frustrating and financially consequential parts of modern care operations. Caldarium focuses specifically on documentation readiness because that is where many avoidable denials begin.</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section id="faq" style={{ background: "rgba(0,0,0,0.3)" }}>
        <div className="section-sm">
          <FadeIn>
            <p className="eyebrow">FAQ</p>
            <h2 className="h2">Questions we hear from healthcare leaders.</h2>
          </FadeIn>
          <div style={{ marginTop: 16 }}>
            {FAQS.map((f) => (
              <FAQ key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FINAL CTA ═══════════════════ */}
      <section>
        <div className="section" style={{ textAlign: "center", paddingTop: 80, paddingBottom: 100 }}>
          <FadeIn>
            <h2 className="h2" style={{ maxWidth: 680, margin: "0 auto 16px" }}>Ready to prevent avoidable denials and protect revenue?</h2>
            <p className="sub" style={{ margin: "0 auto 36px", textAlign: "center" }}>See how Caldarium fits into your prior authorization workflow and where documentation gaps may be costing your team time, rework, and revenue.</p>
            <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
              <a href={CTA_LINK} className="btn-primary">Book a Demo</a>
              <a href={CTA_LINK} className="btn-secondary">Explore a Pilot</a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer style={{ borderTop: "1px solid var(--border)", padding: "40px 24px", textAlign: "center" }}>
        <span className="syne" style={{ fontSize: 16, fontWeight: 700 }}>Caldarium</span>
        <div style={{ display: "flex", gap: 24, justifyContent: "center", margin: "16px 0", fontSize: 13, color: "rgba(255,255,255,0.4)" }}>
          {["Platform", "Use Cases", "Approach", "About", "FAQ"].map((t) => (
            <a key={t} href={`#${t.toLowerCase().replace(/ /g, "-")}`} style={{ color: "inherit", textDecoration: "none" }}>{t}</a>
          ))}
        </div>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.25)" }}>© 2026 Caldarium. All rights reserved.</p>
      </footer>
    </div>
  );
}
